import { Colors } from '@oy/uicore';
import { IconName } from '@oy/uiicon';
import classNames from 'classnames';
import React, {
  DetailedHTMLProps,
  FC,
  forwardRef,
  InputHTMLAttributes,
  Ref,
} from 'react';
import { makeId } from '../../../utilities/idComponent';
import {
  getCustomMessageCSSProperties,
  getMessageIconUrl,
} from '../../../utilities/inputMessage';
import FieldContainer, {
  FieldCustomMessage,
  FontStyle,
} from '../FieldContainer/FieldContainer';
import styles from './TextField.module.css';

export type TextFieldProps = {
  bottomChild?: React.ReactNode;
  customMessage?: FieldCustomMessage | null;
  errorMessage?: string;
  errorMessageId?: string;
  fontStyle?: FontStyle;
  fullWidth?: boolean;
  helpButton?: boolean;
  helpButtonIcon?: IconName;
  helpText?: string;
  helpTextAlign?: 'left' | 'right';
  inputClassName?: string;
  isHorizontal?: boolean;
  isRequired?: boolean;
  label?: string | React.ReactNode;
  labelColor?: Colors;
  leftChild?: React.ReactNode;
  leftColLength?: number;
  rightChild?: React.ReactNode;
  rightIcon?: React.ReactNode;
  shouldUseMessageColor?: boolean;
  showErrorIcon?: boolean;
  subLabel?: string;
  subLabelPosition?: 'vertical' | 'horizontal';
  variant?: 'desktop' | 'mobile';
  warningMessage?: string;
  wrapperClassName?: string;
  onClickHelpButton?: () => void;
  boldLabel?: boolean;
} & DetailedHTMLProps<InputHTMLAttributes<HTMLInputElement>, HTMLInputElement>;

const TextField: FC<TextFieldProps> = forwardRef(
  (
    {
      bottomChild,
      className,
      customMessage,
      errorMessage,
      errorMessageId,
      helpButton,
      helpButtonIcon,
      helpText,
      id,
      inputClassName,
      isRequired,
      label,
      labelColor,
      leftChild,
      leftColLength,
      name,
      onChange,
      onClickHelpButton,
      placeholder,
      rightChild,
      rightIcon,
      shouldUseMessageColor,
      subLabel,
      subLabelPosition = 'vertical',
      warningMessage,
      autoFocus = false,
      disabled = false,
      fontStyle = FontStyle.default,
      fullWidth = false,
      showErrorIcon = true,
      variant = 'desktop',
      wrapperClassName,
      isHorizontal = false,
      boldLabel,
      helpTextAlign,
      ...props
    }: TextFieldProps,
    ref: Ref<HTMLInputElement>,
  ) => {
    const getMessageStyle = () => {
      if (errorMessage) {
        return styles.errorInput;
      } else if (warningMessage) {
        return styles.warningInput;
      } else if (customMessage) {
        return styles.customMessageInput;
      }
      return '';
    };

    const getMessageIcon = () => {
      let icon = '';
      if (errorMessage && showErrorIcon) {
        icon = getMessageIconUrl('error', Colors.RED100);
      } else if (customMessage && customMessage.icon) {
        icon = getMessageIconUrl(customMessage.icon, customMessage.color);
      }
      return icon ? `url("${icon}")` : '';
    };

    const showMessageIcon = () => {
      if (variant === 'mobile' || !showErrorIcon) return false;
      return !!errorMessage || !!customMessage?.icon;
    };

    const classes = classNames(
      className,
      getMessageStyle(),
      {
        [styles.inputContainer]: variant === 'desktop',
        ['font-body-MdRegular']: variant === 'desktop',
        [styles.mobile]: variant === 'mobile',
        [styles.inputWithMessageIcon]: showMessageIcon(),
        [styles.fullWidth]: fullWidth,
      },
      inputClassName,
    );

    return (
      <FieldContainer
        bottomChild={bottomChild}
        className={classNames({
          [styles.mobileContainer]: variant === 'mobile',
        })}
        customMessage={customMessage}
        errorMessage={errorMessage}
        errorMessageId={errorMessageId}
        fontStyle={fontStyle}
        helpButton={helpButton}
        helpButtonIcon={helpButtonIcon}
        helpText={helpText}
        isHorizontal={isHorizontal}
        isRequired={isRequired}
        label={label}
        labelColor={labelColor}
        leftChild={leftChild}
        leftColLength={leftColLength}
        name={name}
        onClickHelpButton={onClickHelpButton}
        rightChild={rightChild}
        shouldUseMessageColor={shouldUseMessageColor}
        subLabel={subLabel}
        subLabelPosition={subLabelPosition}
        warningMessage={warningMessage}
        boldLabel={boldLabel}
        helpTextAlign={helpTextAlign}
      >
        <div
          className={classNames(styles.wrapper, wrapperClassName, {
            [styles.fullWidth]: fullWidth,
          })}
        >
          <input
            id={makeId('ipt', name, id)}
            {...props}
            autoFocus={autoFocus}
            placeholder={placeholder}
            className={classes}
            style={
              {
                '--message-icon': getMessageIcon(),
                ...getCustomMessageCSSProperties(customMessage),
              } as React.CSSProperties
            }
            disabled={disabled}
            name={name}
            onChange={onChange}
            ref={ref}
          />
          {rightIcon && <div>{rightIcon}</div>}
        </div>
      </FieldContainer>
    );
  },
);

export default TextField;
